import { Component, Inject, OnInit, ViewChild } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { BehaviorSubject } from 'rxjs'
import { Actions, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import {
  deleteRentalComplete,
  Destroyable,
  isSomething,
  loadRental,
  Rental,
  selectRentalById,
  selectSome,
  untilDestroy,
  isRestrictingUsers,
  isSavingRental,
  addRentalImagesComplete,
} from '@tokeet-frontend/tv3-platform'
import { map, startWith, switchMap, tap } from 'rxjs/operators'
import { RentalDetailsBasicInfoComponent } from './components/basic-info/rental-details-basic-info.component'
import { RentalDetailsDetailInfoComponent } from './components/detail-info/rental-details-detail-info.component'
import { RentalDetailsImageGalleryComponent } from './components/image-gallery/rental-details-image-gallery.component'
import { RentalDetailsInstructionsComponent } from './components/instructions/rental-details-instructions.component'
import { RentalDetailsCustomInfoComponent } from './components/custom-info/rental-details-custom-info.component'
import { RentalLocationComponent } from './components/location/rental-location.component'
import { RentalComplianceComponent } from './components/compliance/rental-compliance.component'
import * as R from 'ramda'
import { EditRateDialogService } from '@tv3/containers/rates/edit-rate-dialog/edit-rate-dialog.service'
import { FormControl } from '@angular/forms'
import { RentalMultiUnitSettingsComponent } from './components/multi-unit-settings/rental-multi-unit-settings.component'
import { SyncMainUnitData, SyncMainUnitDataConfirmService } from '@tokeet-frontend/rentals'
import { selectActiveProductPlanView } from '@tv3/store/plan/plan.selectors'
import { ProductsForPlan } from '@tv3/store/plan/plan.model'
import { environment } from '@tv3/environments/environment'
import { OpenRateAutomationDialog } from '@tv3/store/overlay/overlay.actions'

export enum RentalDialogTab {
  Calendar,
  Basic,
  Location,
  Custom,
  Info,
  Gallery,
  Instructions,
  Attributes,
  MultiUnitSettings,
  Compliance,
  //
  RatesTable,
  RateMappings,
  Discounts,
  SmartRates,
  Settings,
  //
  Taxes,
  Fees,
  SecurityDeposit,
  PaymentSchedule,
  Rooms,
  UserRestrictions,
  OwnerAccess,
  Tags,
  Connections,
  Calendars,
  Documents,
  CheckinRules, // TV4-3104
  //
}

export const subunitTabs = [
  RentalDialogTab.Calendar,
  RentalDialogTab.Basic,
  RentalDialogTab.Info,
  RentalDialogTab.Gallery,
  RentalDialogTab.Instructions,
  RentalDialogTab.Custom,
  RentalDialogTab.Rooms,
  RentalDialogTab.Tags,
]

@Component({
  selector: 'app-rental-overlay',
  templateUrl: './rental-overlay.component.html',
  styleUrls: ['./rental-overlay.component.scss'],
})
export class RentalOverlayComponent extends Destroyable implements OnInit {
  @ViewChild('basic') basic: RentalDetailsBasicInfoComponent
  @ViewChild('detail') detail: RentalDetailsDetailInfoComponent
  @ViewChild('gallery') gallery: RentalDetailsImageGalleryComponent
  @ViewChild('instructions') instructions: RentalDetailsInstructionsComponent
  @ViewChild('custom') custom: RentalDetailsCustomInfoComponent
  @ViewChild('location') location: RentalLocationComponent
  @ViewChild('multiUnit') multiUnit: RentalMultiUnitSettingsComponent
  @ViewChild('compliance') compliance: RentalComplianceComponent

  tab$ = new BehaviorSubject(RentalDialogTab.Basic)
  tabs = RentalDialogTab

  rental: Rental

  hasIncompleteBaseRate = true
  incompleteBaseRateMessage = $localize`Base Rate settings are required to ensure your rate mappings and future rate pushes go through properly. Please make sure that a nightly rate and minimum stay are set on this tab even if you have no plans to use the Base Settings as a default rate.`

  hasIncompleteCheckInOut = false
  isCheckoutInstructionMissing = false

  isRestrictingUsers$ = this.store.pipe(select(isRestrictingUsers))

  isSaving$ = this.store.pipe(select(isSavingRental))

  isFreePlan$ = this.store.pipe(
    select(selectActiveProductPlanView(ProductsForPlan.AdvanceCM)),
    map((plan) => !plan || plan?.isFree)
  )

  groupRentalId: string
  groupType = 'parent'
  selectedRentalCtrl = new FormControl()
  get incompleteBaseRateTooltip() {
    return this.hasIncompleteBaseRate
      ? this.incompleteBaseRateMessage
      : $localize`Adjust the Base Rate settings for your rental.`
  }

  get instructionsTooltip() {
    return this.hasIncompleteCheckInOut
      ? $localize`This rental has no check-in, check-out or time-zone settings. Please add these details to ensure that any future automations and message templates show the correct times to your guests.`
      : this.isCheckoutInstructionMissing
      ? $localize`Checkout instructions are missing.`
      : $localize`Set the Check-in/Check-out time and add rental details for use with message templates.`
  }

  isChidUnitRental = false
  showCompliance = environment.features.compliance

  constructor(
    private actions: Actions,
    public dialogRef: MatDialogRef<RentalOverlayComponent>,
    private store: Store<fromRoot.State>,
    private editRateDialog: EditRateDialogService,
    private syncMainUnitDataConfirm: SyncMainUnitDataConfirmService,
    @Inject(MAT_DIALOG_DATA) public data: { rentalId: string; activeTab: RentalDialogTab; highlightedImages?: string[] }
  ) {
    super()
    this.selectedRentalCtrl.setValue(this.data.rentalId)
    this.groupRentalId = this.data.rentalId
  }

  ngOnInit() {
    this.tab$.next(this.data.activeTab ?? RentalDialogTab.Basic)
    this.store.dispatch(loadRental({ id: this.data.rentalId }))

    this.selectedRentalCtrl.valueChanges
      .pipe(
        startWith(this.selectedRentalCtrl.value),
        switchMap((id) => this.store.pipe(selectSome(selectRentalById(id)))),
        untilDestroy(this)
      )
      .subscribe((rental) => {
        this.hasIncompleteCheckInOut = !isSomething(rental.checkin) || !isSomething(rental.checkout)
        const baseRate: any = R.pathOr({}, ['baseRate'], rental)
        this.hasIncompleteBaseRate = !isSomething(baseRate.nightly) || !isSomething(baseRate.minimum)
        this.isCheckoutInstructionMissing = !isSomething(rental.instructions?.checkout)
        this.rental = rental
        this.groupRentalId = rental.parent_id || rental.multi_unit_id || rental.id
        this.groupType = rental.multi_unit_id ? 'multi' : rental.parent_id ? 'parent' : ''
        this.isChidUnitRental = !!rental.multi_unit_id && rental.id !== rental.multi_unit_id
        if (this.isChidUnitRental && !subunitTabs.includes(this.tab$.value)) {
          this.tab$.next(RentalDialogTab.Basic)
        }
      })

    this.actions.pipe(ofType(deleteRentalComplete.type), untilDestroy(this)).subscribe(() => {
      this.close()
    })

    this.actions
      .pipe(
        ofType(addRentalImagesComplete),
        switchMap(() => this.syncMainUnitDataConfirm.tryToSyncData(this.rental, SyncMainUnitData.Gallery)),
        untilDestroy(this)
      )
      .subscribe()
  }

  close() {
    this.dialogRef.close()
  }

  onAddStandardRate() {
    this.addRentalRate('standard')
  }

  onAddDynamicRate() {
    this.addRentalRate('dynamic')
  }

  onSmartRates() {
    this.store.dispatch(OpenRateAutomationDialog({}))
  }

  addRentalRate(type: 'standard' | 'dynamic') {
    this.editRateDialog.open({
      rentals: [this.rental],
      rental: this.rental,
      type,
    })
  }
}
